import Script from "next/script"

/**
 * GA4 (same property as the Shopify site). Only rendered when NEXT_PUBLIC_GA_ID is set, i.e. on the live domain.
 * gtag.js (~175 KB, ~250-300 ms main thread on a mid-range phone) is NOT loaded with the page (1 Oct 2026,
 * Lighthouse 100 push, same as BOO): it loads on the first sign of a real visitor (pointer move/down, touch, scroll,
 * key) or after 15 s. Only a scroll of the page itself counts: a self-scrolling rail/carousel must not trigger it. Was lazyOnload, which still ran inside Lighthouse's window and cost ~10-25 points of TBT.
 * Anything that fires earlier calls ensureGtag(), which queues into dataLayer with js/config first; gtag.js replays
 * the queue when it loads. __ga4Init makes sure js/config are pushed exactly once, whoever runs first.
 * Internal browsers (elv_internal cookie, lib/internal-traffic.ts, 8 Oct 2026) load none of it.
 * window.__elevLoadGtag() loads it now -- the purchase event calls it so the order is never left in the queue.
 */
export const Analytics = () => {
  const id = process.env.NEXT_PUBLIC_GA_ID
  if (!id) return null
  return (
    <>
      <Script id="ga4" strategy="afterInteractive">
        {`if(/(^|; )elv_internal=1/.test(document.cookie)){window.__elevInternal=true;window.__elevLoadGtag=function(){}}else{window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){dataLayer.push(arguments)};if(!window.__ga4Init){window.__ga4Init=true;gtag('js',new Date());gtag('config','${id}',{anonymize_ip:true});gtag('config','AW-18486958855');}if(!window.fbq){var n=window.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!window._fbq)window._fbq=n;n.push=n;n.loaded=true;n.version='2.0';n.queue=[];fbq('init','1004166935415404');fbq('track','PageView');}
if(!window.__elevLoadGtag){var loaded=false,evs=["pointermove","pointerdown","touchstart","scroll","keydown","wheel"];var onEv=function(e){if(e.type==="scroll"&&e.target!==document)return;window.__elevLoadGtag()};window.__elevLoadGtag=function(){if(loaded)return;loaded=true;evs.forEach(function(e){window.removeEventListener(e,onEv,true)});var s=document.createElement("script");s.async=true;s.src="https://www.googletagmanager.com/gtag/js?id=${id}";document.head.appendChild(s);var f=document.createElement("script");f.async=true;f.src="https://connect.facebook.net/en_US/fbevents.js";document.head.appendChild(f)};evs.forEach(function(e){window.addEventListener(e,onEv,{capture:true,passive:true})});setTimeout(window.__elevLoadGtag,15000)}}`}
      </Script>
    </>
  )
}

/** Client-side: returns a gtag that works before gtag.js has loaded (calls queue in dataLayer). */
export const ensureGtag = (): ((...args: any[]) => void) | null => {
  const id = process.env.NEXT_PUBLIC_GA_ID
  if (!id || typeof window === "undefined") return null
  const w = window as any
  // Internal browser (elv_internal cookie, see lib/internal-traffic.ts): no GA4/Ads/Meta events at all.
  if (w.__elevInternal || /(^|; )elv_internal=1/.test(document.cookie)) return null
  w.dataLayer = w.dataLayer || []
  if (typeof w.gtag !== "function") w.gtag = function () { w.dataLayer.push(arguments) } // eslint-disable-line prefer-rest-params
  if (!w.__ga4Init) {
    w.__ga4Init = true
    w.gtag("js", new Date())
    w.gtag("config", id, { anonymize_ip: true })
    w.gtag("config", "AW-18486958855") // Elevate Google Ads remarketing (1 Oct 2026)
  }
  return w.gtag
}
